import { useState, useCallback, useEffect } from "react";
import { GET_WORKSPACE_VOICES } from "../services/companies";
import { Voice, VoicesResponse } from "../types/voice";
import { notifications } from "@mantine/notifications";

export interface UseVoiceSelectorResult {
  voices: Voice[];
  defaultVoices: Voice[];
  workspaceVoices: Voice[];
  isLoading: boolean;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  findVoiceByRetellId: (retellVoiceId: string | undefined) => Voice | undefined;
  refreshVoices: () => Promise<void>;
}

/**
 * Hook for managing voice selection with modal state
 * Loads both default and workspace voices
 */
export function useVoiceSelector(): UseVoiceSelectorResult {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [defaultVoices, setDefaultVoices] = useState<Voice[]>([]);
  const [workspaceVoices, setWorkspaceVoices] = useState<Voice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const refreshVoices = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: VoicesResponse = await GET_WORKSPACE_VOICES();
      const defaultVoices = response.default_voices || [];
      const workspaceVoices = response.workspace_voices || [];
      
      setDefaultVoices(defaultVoices);
      setWorkspaceVoices(workspaceVoices);
      setVoices([...defaultVoices, ...workspaceVoices]);
    } catch (error) {
      notifications.show({
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to load voices",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const findVoiceByRetellId = useCallback(
    (retellVoiceId: string | undefined): Voice | undefined => {
      if (!retellVoiceId) return undefined;
      return voices.find((v) => v.retell_voice_id === retellVoiceId);
    },
    [voices]
  );

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  // Load voices on mount
  useEffect(() => {
    refreshVoices();
  }, [refreshVoices]);

  return {
    voices,
    defaultVoices,
    workspaceVoices,
    isLoading,
    isModalOpen,
    openModal,
    closeModal,
    findVoiceByRetellId,
    refreshVoices,
  };
}
